import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import { defineMessages } from "react-intl"
import { BulkActionDefinition, BulkActionType, ColumnDefinition, ColumnDefinitionCategory, DataType, TotalType } from "."
import { ObjectType, ObjectTypeConfig } from ".."
import GlobalHelper from "../../../../common/GlobalHelper"
import { Permissions } from "../../../../role/Guard"
import { BULK_OPERATION_MESSAGES, BULK_OPERATION_TYPE } from "../../../../utils/automation/bulk"
import { PREFERENCE_TYPE } from "../../constants"

export enum SupplierColumnIds {
    NAME = "name",
    CITY = "city",
    PHONE_NUMBER = "phoneNumber",
    CREATED_AT_DATE = "createdAt",
    ARCHIVED = "isArchived",
    UPDATED_AT_DATE = "updatedAt",
    EMAIL = "email",
}

const columnDefinitions: ColumnDefinition[] = [
    {
        id: SupplierColumnIds.NAME,
        sortId: "nameKeyword",
        defaultSort: true,
        dataType: DataType.LINK,
        dataAttributes: {
            redirect: "/suppliers/{id}/details"
        },
        totalAttributes: {
            key: "totalItemsCount",
            type: TotalType.STRING,
        },
        sortable: true,
        identifier: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SupplierColumnIds.CITY,
        accessorFn: row => {
            const address = {
                city: row.city,
                stateCode: row.stateCode,
                state: row.state,
                country: row.country,
                countryCode: row.countryCode,
            }
            return GlobalHelper.formatCityFromAddress(address, AppContext.getLocale().getLanguageWithoutRegionCode())
        },
        sortId: "cityKeyword",
        dataType: DataType.STRING,
        sortable: false,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SupplierColumnIds.PHONE_NUMBER,
        accessorFn: row => {
            if (row.phoneNumber?.length) {
                return row.phoneNumber[0]
            }
            return ""
        },
        sortId: "phoneNumberKeyword",
        dataType: DataType.STRING,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.CONTACT_RELATED],
    },
    {
        id: SupplierColumnIds.CREATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        defaultView: true,
        categories: [ColumnDefinitionCategory.DEFAULT, ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: SupplierColumnIds.ARCHIVED,
        dataType: DataType.BOOLEAN,
        sortable: true,
        categories: [ColumnDefinitionCategory.OTHER],
    },
    {
        id: SupplierColumnIds.UPDATED_AT_DATE,
        dataType: DataType.DATE,
        sortable: true,
        categories: [ColumnDefinitionCategory.DATE_RELATED],
    },
    {
        id: SupplierColumnIds.EMAIL,
        sortId: "emailKeyword",
        dataType: DataType.ARRAY,
        sortable: true,
        categories: [ColumnDefinitionCategory.CONTACT_RELATED],
    },
]

const headerMessages = defineMessages({
    [SupplierColumnIds.NAME]: {
        id: "generic-listing.suppliers.table-header.name",
        defaultMessage: "Name",
    },
    [SupplierColumnIds.CITY]: {
        id: "generic-listing.suppliers.table-header.city",
        defaultMessage: "City",
    },
    [SupplierColumnIds.PHONE_NUMBER]: {
        id: "generic-listing.suppliers.table-header.phone-number",
        defaultMessage: "Phone number",
    },
    [SupplierColumnIds.CREATED_AT_DATE]: {
        id: "generic-listing.suppliers.table-header.created-at-date",
        defaultMessage: "Created at",
    },
    [SupplierColumnIds.ARCHIVED]: {
        id: "generic-listing.suppliers.table-header.archived",
        defaultMessage: "Archived",
    },
    [SupplierColumnIds.UPDATED_AT_DATE]: {
        id: "generic-listing.suppliers.table-header.update-at-date",
        defaultMessage: "Updated at",
    },
    [SupplierColumnIds.EMAIL]: {
        id: "generic-listing.suppliers.table-header.email",
        defaultMessage: "Email",
    },
})

const bulkActionDefinitions: BulkActionDefinition[] = [
    {
        id: BULK_OPERATION_TYPE.DELETE,
        message: BULK_OPERATION_MESSAGES[BULK_OPERATION_TYPE.DELETE],
        icon: "trash",
        type: BulkActionType.DANGER,
        guard: Permissions.DeleteContact
    },
]

const configuration: ObjectTypeConfig = {
    objectType: ObjectType.SUPPLIERS,
    columnDefinitions,
    headerMessages,
    listingPreferencesKey: PREFERENCE_TYPE.LISTING_SUPPLIERS,
    searchPlaceholder: {
        id: "generic-listing.suppliers.search-placeholder",
        defaultMessage: "Search suppliers...",
    },
    displayString: {
        id: "generic-listing.suppliers.display-string",
        defaultMessage: "Suppliers",
    },
    searchObjectType: ObjectType.CONTACTS,
    additionalFilters: {
        contactType: ["suppliers"]
    },
    bulkActionDefinitions,
    creationButtonComponent: require("../../../../common/listing/generic-listing/creation-buttons/SupplierCreationButton"),
}

export default configuration


// WEBPACK FOOTER //
// ./src/state/listing/generic-listing/column-definitions/suppliers.ts